import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, ShieldAlert, Scale, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const INTEREST_AREAS = [
  { k: "family_office", label: "Family office" },
  { k: "institutional", label: "Institutional / fund" },
  { k: "foundation",    label: "Foundation / DFI" },
  { k: "private",       label: "Sophisticated private" },
  { k: "other",         label: "Other" },
];

function EnquiryForm() {
  const [f, setF] = useState({ full_name: "", email: "", organisation: "", country: "", interest_area: "institutional", message: "" });
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!ack) { toast.error("Please acknowledge the regulatory notice."); return; }
    if (!f.full_name || !f.email || !f.country || !f.message) {
      toast.error("Please complete every required field.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/compliance-enquiries", { ...f, acknowledged_no_returns: true });
      setDone(true);
      toast.success(data?.message || "Enquiry received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please email Sebata@pulalove.com directly.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mv-card p-6 md:p-7 flex items-start gap-3" data-testid="enquiry-form-success"
           style={{ background: "linear-gradient(180deg, #fff, rgba(10,59,40,0.05))" }}>
        <Check size={22} className="mt-0.5 mv-green-text"/>
        <div>
          <div className="font-display text-lg mv-green-text">Enquiry received.</div>
          <p className="mt-1 text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
            Our UK compliance desk will contact you within two working days. Reference is stored securely on our servers — you do not need to keep this page open.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mv-card p-6 md:p-7 space-y-4" data-testid="enquiry-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Full name *</span>
          <input value={f.full_name} onChange={on("full_name")} required autoComplete="name"
                 data-testid="enquiry-name"
                 className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"/>
        </label>
        <label className="block">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Email *</span>
          <input type="email" value={f.email} onChange={on("email")} required autoComplete="email"
                 data-testid="enquiry-email"
                 className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"/>
        </label>
        <label className="block">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Organisation</span>
          <input value={f.organisation} onChange={on("organisation")}
                 data-testid="enquiry-org"
                 className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"/>
        </label>
        <label className="block">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Country of residence *</span>
          <input value={f.country} onChange={on("country")} required autoComplete="country-name"
                 data-testid="enquiry-country"
                 className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"/>
        </label>
      </div>
      <label className="block">
        <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Enquiry type</span>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="enquiry-area">
          {INTEREST_AREAS.map((a) => (
            <button key={a.k} type="button" onClick={() => setF({ ...f, interest_area: a.k })}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors ${f.interest_area === a.k ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline"}`}>
              {a.label}
            </button>
          ))}
        </div>
      </label>
      <label className="block">
        <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Brief note on your area of interest *</span>
        <textarea rows={4} value={f.message} onChange={on("message")} required
                  placeholder="What are you exploring, and what timeframe are you working to?"
                  data-testid="enquiry-message"
                  className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"/>
      </label>

      <label className="flex items-start gap-3 cursor-pointer" data-testid="enquiry-ack-row">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
               className="mt-1 w-4 h-4 accent-[color:var(--mv-green)]"
               data-testid="enquiry-ack"/>
        <span className="text-[12.5px]" style={{ color: "var(--mv-ink)" }}>
          I understand that Mavula Investment Ltd is <strong>not FCA-authorised</strong>, does <strong>not offer investment products, yields, dividends or guaranteed returns</strong>, and that this enquiry is not a solicitation. I will take independent regulated advice before making any commitment.
        </span>
      </label>

      <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
        <div className="text-[11.5px]" style={{ color: "var(--mv-muted)" }}>
          Prefer email? Write to <a href="mailto:Sebata@pulalove.com" className="underline mv-green-text">Sebata@pulalove.com</a>.
        </div>
        <button type="submit" disabled={busy || !ack}
                data-testid="enquiry-submit"
                className="btn-green disabled:opacity-50 disabled:cursor-not-allowed">
          <Send size={14}/> {busy ? "Sending…" : "Send enquiry"}
        </button>
      </div>
    </form>
  );
}

/**
 * Institutional / investor enquiries placeholder page.
 * Deliberately contains NO figures, NO yields, NO returns, NO rates.
 * All financial documentation is shared privately after eligibility
 * screening — off-site — pending FCA authorisation.
 */
export default function InvestorsPage() {
  return (
    <main data-testid="investors-page" className="bg-white">
      {/* Hero */}
      <section className="mv-green-bg text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Institutional Enquiries</div>
          <h1 className="font-display text-3xl md:text-5xl mt-4 leading-[1.02]">
            Institutional & investor <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>enquiries.</span>
          </h1>
          <p className="mt-5 text-white/85 text-[14.5px] leading-relaxed max-w-3xl">
            Mavula Investment Ltd does not market investment products, yields or returns on this website. Institutional and eligibility-screened enquiries are handled privately by our compliance desk in the United Kingdom.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 space-y-10">
          {/* Regulatory Notice */}
          <div className="mv-card p-6 md:p-7 flex items-start gap-4"
               style={{ background: "rgba(212,60,60,0.05)", borderColor: "rgba(212,60,60,0.20)" }}
               data-testid="investors-regulatory-notice">
            <ShieldAlert size={22} className="mt-0.5 shrink-0" style={{ color: "#8a2020" }}/>
            <div>
              <div className="font-display text-[16px]" style={{ color: "#8a2020" }}>Important — regulatory notice</div>
              <p className="mt-1 text-[13.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
                Mavula Investment Ltd (UK Company No. 17368822) is <strong>not currently authorised or regulated by the UK Financial Conduct Authority (FCA)</strong>. We do not offer, market, or promise fixed interest, yields, dividends, or investment profits, and nothing on this website is a financial promotion.
                {" "}Read our full <Link to="/legal-status" className="underline mv-green-text">Legal Status</Link> disclosure.
              </p>
            </div>
          </div>

          {/* Enquiry Form */}
          <div>
            <div className="flex items-center gap-3">
              <Mail size={22} className="mv-green-text"/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">Send us an enquiry</h2>
            </div>
            <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
              If you represent a family office, institution, foundation, or sophisticated private investor exploring how you can support Southern African home-ownership infrastructure, please complete the short form below. Our UK compliance desk will respond within two working days with an eligibility questionnaire. Full documentation is shared privately, off-site, only once eligibility is confirmed and independent legal review is arranged.
            </p>

            <div className="mt-6">
              <EnquiryForm />
            </div>
          </div>

          {/* What we work toward */}
          <div>
            <div className="flex items-center gap-3">
              <Scale size={22} className="mv-green-text"/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">What we're working toward</h2>
            </div>
            <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
              We are actively working toward obtaining relevant regulatory authorisations — including <strong>FCA Payment Institution status</strong> in the UK and applicable approvals in Southern African jurisdictions. Any future regulated services will only be launched once all permissions are fully granted. Until then, no returns, yields or investment products are offered.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/legal-status" className="btn-green" data-testid="investors-read-legal">
                Read Legal Status <ArrowRight size={14}/>
              </Link>
              <Link to="/about" className="btn-ghost" data-testid="investors-back-about">
                About Mavula
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
